import { useEffect, useState } from 'react';
import BlogPost, { BlogFAQItem } from '@/models/blog-post';
import { useBlogPost, useCreateBlogPost, useDeleteBlogPost, useUpdateBlogPost, BlogPostMutationPayload } from '@/services/blog';
import { useCategories } from '@/services/categories';
import FormModal from '@/shared/components/form-modal';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { MaterialFormContainer, MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import BlogEditor from '@/shared/components/blog-editor/BlogEditor';
import FaqEditor from './faq-editor';
import { darkPanelStyle, darkSuccessText, darkWarningText, errorBannerStyle} from '@/shared/dark-inline-styles';

interface BlogModalProps {
    isOpen: boolean;
    selectedPostId?: string;
    toggle: () => void;
}

const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const META_TITLE_RANGE: [number, number] = [40, 60];
const META_DESC_RANGE: [number, number] = [120, 160];

interface BlogForm {
    slug: string;
    title: string;
    excerpt: string;
    content: string;
    categoryId: string;
    tagsInput: string;
    authorName: string;
    publishedAt: string; // yyyy-mm-dd (date input) or ''
    readTime: number | '';
    metaTitle: string;
    metaDescription: string;
    ogImage: string; // permanent URL (stored + used as the preview)
    coverImage: string; // permanent URL (stored + used as the preview)
    coverImageAlt: string;
    faq: BlogFAQItem[];
}

const emptyForm = (): BlogForm => ({
    slug: '',
    title: '',
    excerpt: '',
    content: '',
    categoryId: '',
    tagsInput: '',
    authorName: 'Harmonea Team',
    publishedAt: '',
    readTime: '',
    metaTitle: '',
    metaDescription: '',
    ogImage: '',
    coverImage: '',
    coverImageAlt: '',
    faq: [],
});

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p style={{ margin: '-6px 0 10px', fontSize: 12, color: '#999999', lineHeight: 1.5 }}>{children}</p>
);

// Plain-text length of a stripped HTML body — used to decide if content exists.
const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').replace(/&[a-z]+;/gi, ' ').trim();
const countMatches = (html: string, re: RegExp) => (html.match(re) || []).length;
const imagesMissingAlt = (html: string) =>
    (html.match(/<img\b[^>]*>/gi) || []).filter((img) => !/\balt\s*=\s*["'][^"']*\S[^"']*["']/i.test(img)).length;

const BlogModal = ({ isOpen, toggle, selectedPostId }: BlogModalProps) => {
    const { data: post, isLoading } = useBlogPost(selectedPostId as string, isOpen);
    const { mutateAsync: createPost, isPending: isCreating } = useCreateBlogPost();
    const { mutateAsync: updatePost, isPending: isUpdating } = useUpdateBlogPost();
    const { mutateAsync: deletePost, isPending: isDeleting } = useDeleteBlogPost();
    // Blog-scoped categories only — a dropdown (not free text) so a typo can't
    // fork a category into a near-duplicate. Manage the list under Categories.
    const { data: categoriesRes } = useCategories('blog');
    const categories = categoriesRes?.data ?? [];

    const [form, setForm] = useState<BlogForm>(emptyForm());
    const [error, setError] = useState<string | null>(null);
    // In-flight image uploads — Save is blocked while > 0 so a post can't be
    // saved before its cover/OG image URL is committed.
    const [uploadingCount, setUploadingCount] = useState(0);
    const onUploadingChange = (uploading: boolean) => setUploadingCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    const isUploading = uploadingCount > 0;

    // `isOpen` in the deps so re-opening the SAME post re-fills the form from the
    // (cached) data — otherwise neither dep changes on re-open and the
    // close-reset below leaves it blank.
    useEffect(() => {
        if (!isOpen) return;
        if (post?.data) {
            const p: BlogPost = post.data;
            setForm({
                slug: p.slug,
                title: p.title,
                excerpt: p.excerpt,
                content: p.content,
                categoryId: p.categoryId ?? '',
                tagsInput: (p.tags ?? []).join(', '),
                authorName: p.author?.name ?? 'Harmonea Team',
                publishedAt: p.publishedAt ? p.publishedAt.slice(0, 10) : '',
                readTime: typeof p.readTime === 'number' ? p.readTime : '',
                metaTitle: p.seo?.metaTitle ?? '',
                metaDescription: p.seo?.metaDescription ?? '',
                ogImage: p.seo?.ogImage ?? '',
                coverImage: p.coverImage ?? '',
                coverImageAlt: p.coverImageAlt ?? '',
                faq: p.faq ?? [],
            });
        } else if (!selectedPostId) {
            setForm(emptyForm());
        }
        setError(null);
    }, [isOpen, post?.data, selectedPostId]);

    useEffect(() => {
        if (!isOpen) {
            setForm(emptyForm());
            setError(null);
            setUploadingCount(0);
        }
    }, [isOpen]);

    const set = <K extends keyof BlogForm>(key: K, value: BlogForm[K]) => setForm((f) => ({ ...f, [key]: value }));

    // Live SEO checklist — surfaces (doesn't block, except where noted in validate)
    // so the author can't ship a SEO-less post by accident.
    const metaTitleLen = form.metaTitle.trim().length;
    const metaDescLen = form.metaDescription.trim().length;
    const h2Count = countMatches(form.content, /<h2\b/gi);
    const missingAlt = imagesMissingAlt(form.content);
    const checks: { label: string; ok: boolean }[] = [
        { label: `Meta title ${metaTitleLen}/${META_TITLE_RANGE[1]} chars (aim ${META_TITLE_RANGE[0]}–${META_TITLE_RANGE[1]})`, ok: metaTitleLen >= META_TITLE_RANGE[0] && metaTitleLen <= META_TITLE_RANGE[1] },
        { label: `Meta description ${metaDescLen}/${META_DESC_RANGE[1]} chars (aim ${META_DESC_RANGE[0]}–${META_DESC_RANGE[1]})`, ok: metaDescLen >= META_DESC_RANGE[0] && metaDescLen <= META_DESC_RANGE[1] },
        { label: `${h2Count} H2 section heading(s) — aim for ≥2`, ok: h2Count >= 2 },
        { label: missingAlt === 0 ? 'All body images have alt text' : `${missingAlt} body image(s) missing alt text`, ok: missingAlt === 0 },
        { label: 'Slug set', ok: SLUG_RE.test(form.slug.trim()) },
        { label: 'Category selected', ok: !!form.categoryId },
        { label: 'Cover image set', ok: !!form.coverImage },
        { label: 'Excerpt written', ok: form.excerpt.trim().length > 0 },
    ];

    const validate = (): string | null => {
        if (!form.slug.trim()) return 'Slug is required';
        if (!SLUG_RE.test(form.slug.trim())) return 'Slug must be lowercase letters, numbers and hyphens only';
        if (!form.title.trim()) return 'Title is required';
        if (!form.excerpt.trim()) return 'Excerpt is required';
        if (!form.categoryId) return 'Category is required — pick one from the dropdown';
        if (!stripHtml(form.content)) return 'Post body is required';
        if (/<h1\b/i.test(form.content)) return 'Remove the H1 from the body — the post title is already the page heading. Use H2/H3.';
        if (imagesMissingAlt(form.content) > 0) return 'Every body image needs alt text (SEO + accessibility). Re-insert the image(s) with a description.';
        if (!form.metaTitle.trim()) return 'SEO meta title is required';
        if (!form.metaDescription.trim()) return 'SEO meta description is required';
        if (form.coverImage && !form.coverImageAlt.trim()) return 'Cover image needs alt text';
        // readTime is optional ("auto if blank") but, if set, must be a whole
        // minute ≥ 1 — otherwise the API rejects it with an opaque 400.
        if (form.readTime !== '' && (!Number.isInteger(Number(form.readTime)) || Number(form.readTime) < 1)) {
            return 'Read time must be a whole number of minutes (1 or more), or leave it blank to auto-calculate.';
        }
        return null;
    };

    const buildPayload = (): BlogPostMutationPayload => ({
        slug: form.slug.trim(),
        title: form.title.trim(),
        excerpt: form.excerpt.trim(),
        content: form.content,
        categoryId: form.categoryId,
        tags: form.tagsInput
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
        author: { name: form.authorName.trim() || 'Harmonea Team' },
        publishedAt: form.publishedAt ? new Date(form.publishedAt).toISOString() : undefined,
        readTime: form.readTime === '' ? undefined : Number(form.readTime),
        seo: {
            metaTitle: form.metaTitle.trim(),
            metaDescription: form.metaDescription.trim(),
            ogImage: form.ogImage || undefined,
        },
        faq: form.faq.filter((f) => f.q.trim() && f.a.trim()).length ? form.faq.filter((f) => f.q.trim() && f.a.trim()) : undefined,
        coverImage: form.coverImage || undefined,
        coverImageAlt: form.coverImageAlt.trim() || undefined,
    });

    const handleSave = async () => {
        if (isUploading) {
            setError('Please wait — an image is still uploading.');
            return;
        }
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            const payload = buildPayload();
            // callApi never throws on API errors — it resolves { status:'error' },
            // so a try/catch alone misses 400/409/etc. Inspect the result and
            // surface the backend message instead of closing as if it saved.
            const result = selectedPostId ? await updatePost({ postId: selectedPostId, ...payload }) : await createPost(payload);
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to save the post.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to save blog post:', e);
            setError('Failed to save the post. Check the console for details.');
        }
    };

    const handleDelete = async () => {
        if (!selectedPostId) return;
        try {
            const result = await deletePost({ postId: selectedPostId });
            if (result.status === 'error') {
                setError(result.error?.message ?? 'Failed to delete the post.');
                return;
            }
            toggle();
        } catch (e) {
            console.error('Failed to delete blog post:', e);
            setError('Failed to delete the post. Check the console for details.');
        }
    };

    return (
        <FormModal
            isOpen={isOpen}
            showDelete={!!selectedPostId}
            title={`${selectedPostId ? 'Edit' : 'New'} Blog Post`}
            body={
                <MaterialFormContainer onSubmit={handleSave}>
                    {error && <div style={errorBannerStyle}>{error}</div>}

                    <MaterialTextField label="Title" value={form.title} onChange={(e) => set('title', e.target.value)} />
                    <Hint>Write for humans first; put the main keyword near the front.</Hint>

                    <MaterialTextField label="Slug (URL: /blog/your-slug)" value={form.slug} onChange={(e) => set('slug', e.target.value)} />
                    <Hint>
                        Short, lowercase, hyphenated, keyword-rich. ⚠️ Don’t change it after publishing — it breaks the link and loses Google ranking.
                    </Hint>

                    <MaterialTextField label="Excerpt (summary on the blog list & link previews)" multiline value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} />

                    <MaterialTextField name="select" select label="Category" value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
                        <MaterialFormOption value="">— Select a category —</MaterialFormOption>
                        {categories.map((c) => (
                            <MaterialFormOption key={c.id} value={c.id}>
                                {c.name}
                            </MaterialFormOption>
                        ))}
                    </MaterialTextField>
                    {categories.length === 0 && (
                        <Hint>
                            No blog categories yet. Add one under <strong>Categories</strong> (set its Type to <strong>Blog</strong>), then pick it here.
                        </Hint>
                    )}

                    <MaterialTextField label="Tags (comma-separated)" value={form.tagsInput} onChange={(e) => set('tagsInput', e.target.value)} />

                    <MaterialTextField label="Author name" value={form.authorName} onChange={(e) => set('authorName', e.target.value)} />

                    <div style={{ display: 'flex', gap: 16 }}>
                        <MaterialTextField label="Publish date" type="date" value={form.publishedAt} onChange={(e) => set('publishedAt', e.target.value)} />
                        <MaterialTextField
                            label="Read time (min — auto if blank)"
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            value={form.readTime}
                            onChange={(e) => set('readTime', e.target.value === '' ? '' : Number(e.target.value))}
                        />
                    </div>

                    {/* ── Body ─────────────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Body</p>
                    <Hint>
                        Use <strong>Heading 2</strong> for sections, <strong>Heading 3</strong> for sub-sections — never Heading 1 (the title is already the page’s H1). Every image needs alt text.
                    </Hint>
                    <BlogEditor value={form.content} onChange={(html) => set('content', html)} />

                    {/* ── Cover image ──────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>Cover image</p>
                    <Hint>Shown at the top of the post and as the social-share image. Add alt text describing it.</Hint>
                    <DropZoneField
                        customHeight
                        usePermanentUrl
                        uploadPath="blog/images"
                        accept="image/jpeg, image/jpg, image/png, image/webp"
                        currentValue={form.coverImage}
                        currentFileType="image"
                        onChange={(coverImage) => set('coverImage', coverImage)}
                        onUploadingChange={onUploadingChange}
                    />
                    <MaterialTextField label="Cover image alt text" value={form.coverImageAlt} onChange={(e) => set('coverImageAlt', e.target.value)} />

                    {/* ── SEO ──────────────────────────────────────────────── */}
                    <p style={{ margin: '18px 0 4px', fontWeight: 600 }}>SEO</p>
                    <MaterialTextField label={`Meta title (${metaTitleLen} chars)`} value={form.metaTitle} onChange={(e) => set('metaTitle', e.target.value)} />
                    <Hint>Shown in Google results & the browser tab. Aim {META_TITLE_RANGE[0]}–{META_TITLE_RANGE[1]} characters. Include the keyword.</Hint>
                    <MaterialTextField
                        label={`Meta description (${metaDescLen} chars)`}
                        multiline
                        value={form.metaDescription}
                        onChange={(e) => set('metaDescription', e.target.value)}
                    />
                    <Hint>Shown under the title in Google. Aim {META_DESC_RANGE[0]}–{META_DESC_RANGE[1]} characters. Summarize the post and invite the click.</Hint>

                    <p style={{ margin: '8px 0 4px' }}>Social share image (optional — defaults to the cover image)</p>
                    <DropZoneField
                        customHeight
                        usePermanentUrl
                        uploadPath="blog/images"
                        accept="image/jpeg, image/jpg, image/png, image/webp"
                        currentValue={form.ogImage}
                        currentFileType="image"
                        onChange={(ogImage) => set('ogImage', ogImage)}
                        onUploadingChange={onUploadingChange}
                    />

                    {/* ── FAQ ──────────────────────────────────────────────── */}
                    <div style={{ marginTop: 18 }}>
                        <FaqEditor faq={form.faq} onChange={(faq) => set('faq', faq)} />
                    </div>

                    {/* ── SEO checklist ────────────────────────────────────── */}
                    <div style={darkPanelStyle}>
                        <p style={{ margin: '0 0 8px', fontWeight: 600 }}>SEO checklist</p>
                        {checks.map((c) => (
                            <div key={c.label} style={{ fontSize: 13, color: c.ok ? darkSuccessText : darkWarningText, marginBottom: 4 }}>
                                {c.ok ? '✅' : '⚠️'} {c.label}
                            </div>
                        ))}
                    </div>

                    <Hint>
                        Saving makes the post live on the site immediately. To pull it down, delete it (or fix it by editing). The social-share preview
                        (X/LinkedIn) updates after the next production deploy.
                    </Hint>
                </MaterialFormContainer>
            }
            toggle={toggle}
            handleSave={handleSave}
            handleDelete={handleDelete}
            loading={isLoading || isCreating || isUpdating || isDeleting}
            saveDisabled={isUploading}
            saveDisabledHint="Uploading image… please wait until it finishes before saving."
        />
    );
};

export default BlogModal;
